import React, { useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateOnly } from '../../utils';
import { openStoredQuotePdf, quoteStatusColor, quoteStatusLabel } from './quoteUtils';

/**
 * "Tarjous lähetetty" box on the work order page (quote state only).
 *
 * Driven by `latest_quote` (the newest snapshot version) and falls back to the
 * event-based `last_offer_sent` for quotes sent before snapshots existed.
 */
function QuoteSentBanner({ maintenance, onError, onShowVersions }) {
  const { t } = useTranslation();
  const [opening, setOpening] = useState(false);
  const latest = maintenance?.latest_quote;
  const legacy = maintenance?.last_offer_sent;

  if (!latest && !legacy) return null;

  const handleOpenPdf = async () => {
    setOpening(true);
    try {
      await openStoredQuotePdf(latest);
    } catch (err) {
      if (onError) onError(t('Failed to generate offer'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <Box
      data-testid="quote-sent-banner"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        flexWrap: 'wrap',
        p: 2,
        mt: 3,
        bgcolor: 'rgba(16, 185, 129, 0.08)',
        borderRadius: '10px',
        border: '1px solid rgba(16, 185, 129, 0.2)',
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.main', flexShrink: 0, mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 200 }}>
        {latest ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {t('quoteSnapshot.sentTo', { recipient: latest.recipient_email || latest.recipient_phone })}
              {' · '}
              {formatDate(latest.sent_at)}
            </Typography>
            {latest.valid_until && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('quoteSnapshot.validUntil', { date: formatDateOnly(latest.valid_until) })}
              </Typography>
            )}
            {latest.differs_from_current && (
              <Typography
                variant="caption"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.dark', mt: 0.5 }}
                data-testid="quote-differs-warning"
              >
                <WarningAmberIcon sx={{ fontSize: 16 }} />
                {t('quoteSnapshot.differsFromSent')}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('Offer sent')}: {legacy.recipient}
            {' · '}
            {formatDate(legacy.sent_at)}
          </Typography>
        )}
      </Box>
      {latest && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            variant="outlined"
            color={quoteStatusColor(latest)}
            label={quoteStatusLabel(latest, t)}
            onClick={onShowVersions}
          />
          <Button
            size="small"
            variant="text"
            startIcon={<PictureAsPdfIcon fontSize="small" />}
            onClick={handleOpenPdf}
            disabled={opening}
          >
            {t('quoteSnapshot.openPdf')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default QuoteSentBanner;
